import Big from 'big.js';
import { AllocationValueTypeEnum } from '../types/types';
import { logger } from '../utils';
import type { SubAccount } from './SubAccount';

export const EMPTY_SUB_ACCOUNT_SELECTION = '';
/** A default empty sub account allocation entry */
export const DEFAULT_SUBACCOUNT_ALLOCATION = { subAccount: EMPTY_SUB_ACCOUNT_SELECTION, value: '100' };
export const EMPTY_SUBACCOUNT_ALLOCATION = { subAccount: EMPTY_SUB_ACCOUNT_SELECTION, value: '0' };

/** A frontend-specific representation of an Allocation to Sub Accounts */
export interface Allocation {
  subAccount: string;
  value: string;
}

/** Backend representation of Allocation */
export interface AllocationBE {
  ValueType: AllocationValueTypeEnum;
  Allocations: { SubAccount: string; Value: string }[];
}

/**
 * For entities that have SubAccount | Allocation, this function just grabs all the sub account names
 * If neither exist on the entity, undefined will be returned
 */
export function getEntitySubAccountNames({
  SubAccount,
  Allocation,
}: {
  SubAccount?: string;
  Allocation?: AllocationBE;
}): string[] | undefined {
  if (SubAccount) {
    return [SubAccount];
  }
  if (Allocation) {
    return Allocation.Allocations.map(alloc => alloc.SubAccount);
  }
  return undefined;
}

/**
 * Given that you've received a backend entity containing something like
 * `{ SubAccount: string, Allocation: IAllocation }`, where either but not both of these are defined (the usual),
 * this function will convert this construct into just a frontend Allocations array and an allocation value type for you to work with.
 *
 * If both are undefined, it will return the default frontend allocations construct, which is:
 * `{ subAccountAllocations: [{ subAccount: "", value: "100" }], allocationValueType: "Percentage" }`
 */
export function convertSubAccountOrAllocationForUI(
  subAccount: string | undefined,
  allocation: AllocationBE | undefined
) {
  if (subAccount) {
    return {
      allocationValueType: AllocationValueTypeEnum.Percentage,
      subAccountAllocations: [{ subAccount, value: '100' }],
    };
  }

  if (allocation) {
    return convertReceivedAllocationForUI(allocation);
  }

  return {
    allocationValueType: AllocationValueTypeEnum.Percentage,
    subAccountAllocations: [DEFAULT_SUBACCOUNT_ALLOCATION],
  };
}

/**
 * Given an IAllocation, returns a frontend usable array of subAccountAllocations along side the allocationValueType
 *
 * Percent params 0.2 --> 20
 *
 * allocation.valueType --> allocationValueType
 *
 * allocation.allocations --> subAccountAllocations
 */
export function convertReceivedAllocationForUI(allocation: AllocationBE) {
  const allocations = allocation.Allocations;
  const allocationValueType = allocation.ValueType;
  const subAccountAllocations: Allocation[] = [];

  if (allocationValueType === AllocationValueTypeEnum.Percentage) {
    for (let i = 0; i < allocations.length; i++) {
      subAccountAllocations.push({
        subAccount: allocations[i].SubAccount,
        value: Big(allocations[i].Value || '0')
          .times(100)
          .toFixed(),
      });
    }
  } else {
    for (let i = 0; i < allocations.length; i++) {
      subAccountAllocations.push({
        subAccount: allocations[i].SubAccount,
        value: allocations[i].Value || '',
      });
    }
  }

  return {
    subAccountAllocations,
    allocationValueType,
  };
}

/**
 * Converts the frontend Allocation structure into the backend-supported formats for making requests
 *
 * Parameter quantity is optional -- setting it to undefined is allowed, but it should only be used if you only intend
 * to use the Percentage AllocationValueTypeEnum.
 */
export const frontendAllocationsToBackendAllocations = ({
  subAccountAllocations,
  subAccounts,
  allocationValueType,
  quantity,
}: {
  subAccountAllocations?: Allocation[];
  /** Provide an array of all sub accounts if you want to validate the entries in the allocations array before sending them */
  subAccounts: SubAccount[] | undefined;
  allocationValueType?: AllocationValueTypeEnum;
  quantity: string | undefined;
}): AllocationBE | undefined => {
  if (allocationValueType == null) {
    logger.error(new Error(`allocationValueType is null while preparing allocations`));
    return undefined;
  }
  if (subAccountAllocations == null) {
    logger.error(new Error(`subAccountAllocations is null while preparing allocations`));
    return undefined;
  }
  if (allocationValueType === AllocationValueTypeEnum.Quantity && quantity == null) {
    logger.error(new Error(`Trying to prepare Allocations of type Quantity without providing a quantity`));
    return undefined;
  }
  const allocationsArr: AllocationBE['Allocations'] = [];

  if (subAccountAllocations?.length === 1) {
    const subAccount = subAccountAllocations[0].subAccount;
    if (!subAccount) {
      logger.error(new Error(`Single allocation is incorrectly formatted`), {
        extra: {
          allocation: subAccountAllocations[0],
        },
      });
      return undefined;
    }

    if (allocationValueType === AllocationValueTypeEnum.Percentage) {
      return {
        ValueType: allocationValueType,
        Allocations: [{ SubAccount: subAccount, Value: '1' }],
      };
    }

    if (allocationValueType === AllocationValueTypeEnum.Quantity && quantity != null) {
      return {
        ValueType: allocationValueType,
        Allocations: [{ SubAccount: subAccount, Value: quantity }],
      };
    }

    return undefined;
  }

  if (allocationValueType === AllocationValueTypeEnum.Percentage) {
    for (let i = 0; i < subAccountAllocations.length; i++) {
      const percentValue = subAccountAllocations[i].value || '100';
      const subAccount = subAccountAllocations[i].subAccount;
      const value = Big(percentValue).div(100).toFixed();
      if (!subAccount) {
        logger.error(new Error(`Allocation is incorrectly formatted`), {
          extra: {
            allocation: JSON.stringify(subAccountAllocations[i]),
          },
        });
        return undefined;
      }
      allocationsArr.push({ SubAccount: subAccount, Value: value });
    }
  } else {
    for (let i = 0; i < subAccountAllocations.length; i++) {
      const value = subAccountAllocations[i].value || quantity;
      if (value == null) {
        return undefined;
      }

      const subAccount = subAccountAllocations[i].subAccount;
      if (!subAccount) {
        logger.error(new Error(`Allocation is incorrectly formatted`), {
          extra: {
            allocation: JSON.stringify(subAccountAllocations[i]),
          },
        });
        return undefined;
      }
      allocationsArr.push({ SubAccount: subAccount, Value: value });
    }
  }

  // Validation for sending allocations array
  for (const allocation of allocationsArr) {
    const allocHasCorrectEntries = allocation && allocation.SubAccount && allocation.Value;
    // Only validate the existence if the implementer has provided a sub accounts array. We don't always have to do this. Kinda weird to begin with.
    const subAccountExistenceValid =
      subAccounts == null ? true : subAccounts.some(sa => sa.Name === allocation.SubAccount);
    if (!allocHasCorrectEntries || !subAccountExistenceValid) {
      logger.error(new Error(`Allocation failed validation`), {
        extra: {
          allocation: JSON.stringify(allocation),
        },
      });
      return undefined;
    }
  }

  return {
    ValueType: allocationValueType,
    Allocations: allocationsArr,
  };
};
